/**
 * Workforce Bench status model — the FIVE independent axes.
 *
 * A workforce employee is org-owned and can be assigned to multiple projects at
 * once, so a single `working / available` status is wrong. The global Bench and
 * the global Employee Profile model status as five separately-derived axes:
 *
 *   1. Lifecycle   — the ONLY global/durable org state.
 *   2. Load        — active assignments from workforce team memberships.
 *   3. Attention   — aggregated "what needs a human?", traced to a project.
 *   4. Assignments — where the employee is deployed right now.
 *   5. CurrentFocus— a single hint (NOT a status).
 *
 * Project-local `working / review / blocked` is correct INSIDE a project and
 * lives in `./status.ts` + the project-scoped pages — it is intentionally not
 * reused here.
 *
 * Spec: design/design_handoff_workforce_navigation/01-status-model.md
 */

// 1) LIFECYCLE — org-owned state. The only durable global status.
export type Lifecycle = 'active' | 'paused' | 'needs_setup' | 'terminated';

// 2) LOAD — active assignments from workforce team memberships.
export type LoadState = 'assigned' | 'unassigned' | 'at_capacity' | 'over_capacity' | 'frozen';
export interface Load {
  activeAssignments: number;
  state: LoadState;
  capacity?: number | null;
  headroom?: number | null;
}

// 3) ATTENTION — aggregated across all projects.
export type AttentionLevel = 'clear' | 'needs_review' | 'blocked' | 'permission_needed';
export interface Attention {
  level: AttentionLevel;
  detail: string | null;
  sourceProject: string | null;
}

// 4) ASSIGNMENTS — where they are deployed right now.
export interface Assignment {
  projectId: string;
  projectName: string;
  active: boolean;
  /** Project-scoped team the membership belongs to, when known. */
  team?: string | null;
  /** ISO timestamp the assignment started (team_members.joinedAt). */
  since?: string | null;
  /** ISO timestamp the assignment ended (team_members.leftAt), null while active. */
  endedAt?: string | null;
}

// 5) CURRENT FOCUS — a single hint, never a status.
export interface CurrentFocus {
  label: string | null;
  projectId: string | null;
  projectName?: string | null;
}

export interface FleetSummary {
  totalOwned: number;
  aiCount: number;
  humanCount: number;
  byLifecycle: Record<Lifecycle, number>;
  assignedEmployees: number;
  unassignedEmployees: number;
  activeAssignments: number;
  needsAttention: number;
  attentionBreakdown: Record<Exclude<AttentionLevel, 'clear'>, number>;
}

// ── Lifecycle derivation ────────────────────────────────────────────────────

export const LIFECYCLE_META: Record<Lifecycle, { label: string; dot: string; pill: PillTone }> = {
  active: { label: 'Active', dot: 'bg-success', pill: 'ok' },
  paused: { label: 'Paused', dot: 'bg-warning', pill: 'warn' },
  needs_setup: { label: 'Needs setup', dot: 'bg-purple', pill: 'purple' },
  terminated: { label: 'Terminated', dot: 'bg-faint', pill: 'neutral' },
};

export const LOAD_STATE_META: Record<LoadState, { label: string; pill: PillTone }> = {
  unassigned: { label: 'Unassigned', pill: 'neutral' },
  assigned: { label: 'Assigned', pill: 'ok' },
  at_capacity: { label: 'At capacity', pill: 'warn' },
  over_capacity: { label: 'Over capacity', pill: 'danger' },
  frozen: { label: 'Frozen', pill: 'neutral' },
};

/**
 * Derive the durable org lifecycle from the real daemon row fields:
 *   lifecycleStatus (entityLifecycle: draft|active|inactive|archived),
 *   status (agentStatus: idle|running|waiting|error|paused|terminated).
 */
export function deriveLifecycle(raw: Record<string, unknown>): Lifecycle {
  const lifecycleStatus = typeof raw.lifecycleStatus === 'string' ? raw.lifecycleStatus : null;
  const runtimeStatus = typeof raw.status === 'string' ? raw.status : null;
  if (lifecycleStatus === 'archived' || runtimeStatus === 'terminated') return 'terminated';
  if (lifecycleStatus === 'draft') return 'needs_setup';
  if (lifecycleStatus === 'inactive' || runtimeStatus === 'paused') return 'paused';
  return 'active';
}

// ── Load / assignments ──────────────────────────────────────────────────────

/**
 * Load is intentionally assignment-based until the schema has a real
 * concurrent-assignment capacity field. Do not derive capacity from tier text.
 */
export function deriveLoadState(lifecycle: Lifecycle, activeAssignments: number): LoadState {
  if (lifecycle !== 'active') return 'frozen';
  return activeAssignments > 0 ? 'assigned' : 'unassigned';
}

// ── Attention ───────────────────────────────────────────────────────────────

const ATTENTION_SEVERITY: Record<AttentionLevel, number> = {
  blocked: 3,
  permission_needed: 2,
  needs_review: 1,
  clear: 0,
};

export function moreSevere(a: AttentionLevel, b: AttentionLevel): AttentionLevel {
  return ATTENTION_SEVERITY[a] >= ATTENTION_SEVERITY[b] ? a : b;
}

export const ATTENTION_META: Record<
  AttentionLevel,
  { label: string; icon: 'check' | 'eye' | 'alert' | 'shield-x'; text: string }
> = {
  clear: { label: 'Clear', icon: 'check', text: 'text-faint' },
  needs_review: { label: 'Needs review', icon: 'eye', text: 'text-warning-text' },
  blocked: { label: 'Blocked', icon: 'alert', text: 'text-danger-text' },
  permission_needed: { label: 'Permission needed', icon: 'shield-x', text: 'text-purple-text' },
};

// ── Bench filters ───────────────────────────────────────────────────────────

export type BenchFilter = 'all' | 'attention' | 'assigned' | 'unassigned' | 'paused' | 'needs_setup';

export interface BenchEmployee {
  lifecycle: Lifecycle;
  load: Load;
  attention: Attention;
}

export function matchesBenchFilter(emp: BenchEmployee, filter: BenchFilter): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'attention':
      return emp.attention.level !== 'clear';
    case 'assigned':
      return emp.lifecycle === 'active' && emp.load.activeAssignments > 0;
    case 'unassigned':
      return emp.lifecycle === 'active' && emp.load.activeAssignments === 0;
    case 'paused':
      return emp.lifecycle === 'paused';
    case 'needs_setup':
      return emp.lifecycle === 'needs_setup';
    default:
      return true;
  }
}

/**
 * Default Bench sort: attention first (blocked → permission → review), then
 * employees already assigned to work, then the rest. Terminated sinks to the bottom.
 */
export function benchSortValue(emp: BenchEmployee): number {
  if (emp.lifecycle === 'terminated') return -100;
  let score = ATTENTION_SEVERITY[emp.attention.level] * 10;
  if (emp.lifecycle === 'active' && emp.load.state === 'assigned') score += 1;
  return score;
}

// ── Fleet summary (computed org-wide, client-side) ──────────────────────────

export function computeFleetSummary(employees: Array<BenchEmployee & { kind: 'human' | 'ai' }>): FleetSummary {
  const byLifecycle: Record<Lifecycle, number> = { active: 0, paused: 0, needs_setup: 0, terminated: 0 };
  const attentionBreakdown: Record<Exclude<AttentionLevel, 'clear'>, number> = {
    blocked: 0,
    permission_needed: 0,
    needs_review: 0,
  };
  let aiCount = 0;
  let humanCount = 0;
  let assignedEmployees = 0;
  let unassignedEmployees = 0;
  let activeAssignments = 0;
  let needsAttention = 0;

  for (const emp of employees) {
    byLifecycle[emp.lifecycle] += 1;
    if (emp.kind === 'human') humanCount += 1;
    else aiCount += 1;
    if (emp.lifecycle === 'active') {
      activeAssignments += emp.load.activeAssignments;
      if (emp.load.activeAssignments > 0) {
        assignedEmployees += 1;
      } else {
        unassignedEmployees += 1;
      }
    }
    if (emp.attention.level !== 'clear') {
      needsAttention += 1;
      attentionBreakdown[emp.attention.level] += 1;
    }
  }

  return {
    totalOwned: employees.length,
    aiCount,
    humanCount,
    byLifecycle,
    assignedEmployees,
    unassignedEmployees,
    activeAssignments,
    needsAttention,
    attentionBreakdown,
  };
}

/** Pill tone vocabulary shared by the comp-faithful Pill atom. */
export type PillTone = 'ok' | 'warn' | 'danger' | 'accent' | 'purple' | 'teal' | 'neutral';
